import { z } from 'zod';
import { apiErrorCodeSchema } from '../api/error.js';
import { MESSAGE_CONTENT_MAX } from '../constants.js';
import { idSchema } from '../domain/primitives.js';
import { reactionSchema } from '../domain/message.js';
import { channelReceiptSchema, receiptKindSchema } from '../domain/receipt.js';
/**
 * Chat event payloads.
 *
 * `chat.message` reuses the domain shapes directly - `SendMessageInput` going up
 * and `Message` coming down - so they are not restated here. So does
 * `chat.messageEdited`, which carries the whole updated `Message`.
 *
 * `chat.error` exists because a socket emit has no status code. Without it, a
 * rejected send (not a member, malformed payload) would be silently dropped and
 * the sender's UI would show a message that was never stored.
 */
export const chatErrorSchema = z.object({
    /** Same vocabulary as HTTP failures, so clients branch on one set of codes. */
    code: apiErrorCodeSchema,
    message: z.string(),
    /** The channel the failed send targeted, when it was parseable. */
    channelId: idSchema.optional(),
});
// ---- Acknowledged requests -------------------------------------------------
/**
 * The answer to an acknowledged chat request (`chat.edit`, `chat.delete`).
 *
 * Those two use a Socket.IO acknowledgement rather than `chat.error`: the
 * contract carries no correlation id, so a `chat.error` could not say WHICH edit
 * failed. An ack is bound to its own request, so the answer cannot be misfiled.
 */
export const chatAckSchema = z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true) }),
    z.object({ ok: z.literal(false), code: apiErrorCodeSchema, message: z.string() }),
]);
/**
 * `chat.edit`: replace the text of one of your own messages.
 *
 * Only `text` messages can be edited - a `file-share-event` records a transfer
 * that happened, and rewriting it would make the transcript lie. The server
 * applies the `maxMessageLength` setting on top of this hard ceiling.
 */
export const editMessageInputSchema = z.object({
    channelId: idSchema,
    messageId: idSchema,
    content: z.string().min(1).max(MESSAGE_CONTENT_MAX),
});
/** `chat.delete`: remove one of your own messages, for everyone. */
export const deleteMessageInputSchema = z.object({
    channelId: idSchema,
    messageId: idSchema,
});
/** Server -> clients: a message is gone. Its id is all a transcript needs to drop it. */
export const messageDeletedSchema = z.object({
    channelId: idSchema,
    messageId: idSchema,
});
// ---- Receipts --------------------------------------------------------------
/**
 * `chat.receipt` going up: "I have received / read up to this message".
 *
 * Addressed by message id rather than by timestamp, so a client can only ever
 * claim to have reached a message that exists - the server moves the cursor to
 * that message's own `createdAt`, never to a time the client supplied.
 */
export const receiptInputSchema = z.object({
    channelId: idSchema,
    messageId: idSchema,
    kind: receiptKindSchema,
});
/** `chat.receipt` coming down: one member's cursors moved. */
export const receiptUpdateSchema = channelReceiptSchema.extend({
    channelId: idSchema,
});
// ---- Administrative changes ------------------------------------------------
/**
 * A transcript was emptied by an administrator.
 *
 * `channelId: null` means EVERY channel - the "clear all history" action - so
 * one broadcast covers it instead of one per channel.
 */
export const historyClearedSchema = z.object({
    channelId: idSchema.nullable(),
});
/**
 * This user can no longer see a channel: it was deleted, or they were removed
 * from it. Either way the client drops it from the sidebar and its transcript.
 */
export const channelRemovedSchema = z.object({
    channelId: idSchema,
    reason: z.enum(['deleted', 'removed']),
});
// ---- Typing ------------------------------------------------------------------
/**
 * `chat.typing` going up: "I am typing in this channel". Sent at most every few
 * seconds while the user types; there is no "stopped" event - it simply lapses.
 */
export const typingInputSchema = z.object({
    channelId: idSchema,
});
/** `chat.typing` coming down: who is typing where. Never echoed to the typist. */
export const typingUpdateSchema = z.object({
    channelId: idSchema,
    userId: idSchema,
});
/** How long a typing signal lasts on screen without a fresh one. */
export const TYPING_TTL_MS = 5_000;
// ---- Reactions ---------------------------------------------------------------
/**
 * `chat.react`: set your reaction on a message, or clear it with `emoji: null`.
 * One reaction per person per message, so choosing another replaces it.
 */
export const reactInputSchema = z.object({
    channelId: idSchema,
    messageId: idSchema,
    emoji: z.string().min(1).max(16).nullable(),
});
/** `chat.reactions`: a message's reactions changed. Carries the whole new set. */
export const reactionsUpdateSchema = z.object({
    channelId: idSchema,
    messageId: idSchema,
    reactions: z.array(reactionSchema),
});
// ---- Forwarding --------------------------------------------------------------
/** `chat.forward`: copy a message you can see into channels you are in. */
export const forwardInputSchema = z.object({
    channelId: idSchema,
    messageId: idSchema,
    targetChannelIds: z.array(idSchema).min(1).max(20),
});
//# sourceMappingURL=chat.js.map